import { graphql } from 'gatsby';
import React from 'react';
import {
  processImageAsset,
  processMandatoryImage,
  processOptionalCta,
} from 'helpers/csTypeProcessors';
import { nonFatalBuildError } from 'helpers/errorReporting';
import { CsAsset, CsCta } from 'types/contentStack';
import { StyledImageAndCtaCards } from './styles';

const activeEnv = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || 'development';
const isPreview = activeEnv === 'preview';

type ImageAndCtaCardsBlockProps = {
  image_and_cta_cards: {
    heading: string;
    intro: string | null;
    html_id?: string;
    cards: {
      image: CsAsset;
      heading: string;
      body: string;
      primary_cta: [CsCta] | null;
      secondary_cta: [CsCta] | null;
    }[];
  };
};

export const query = graphql`
  fragment ImageAndCtaCardsBlock on cs__image_and_cta_cards {
    heading
    intro
    html_id
    cards {
      image {
        ...Asset
      }
      heading
      body
      primary_cta {
        ...Cta
      }
      secondary_cta {
        ...Cta
      }
    }
  }
`;

const ImageAndCtaCardsBlock: React.FC<ImageAndCtaCardsBlockProps> = ({
  image_and_cta_cards,
}) => {
  const cards = image_and_cta_cards.cards
    .map((card, index) => {
      const image = isPreview
        ? processImageAsset(card.image)
        : processMandatoryImage(card.image);
      if (!image) {
        nonFatalBuildError(
          'Image for Image and CTA card missing!',
          `Image and CTA Card ${index + 1} needs an image in order to be displayed.`
        );
        return;
      }
      return {
        image,
        heading: card.heading,
        body: card.body,
        primaryCta: processOptionalCta(card.primary_cta, 'ImageAndCtaCardsPrimaryCta'),
        secondaryCta: processOptionalCta(
          card.secondary_cta,
          'ImageAndCtaCardsSecondaryCta'
        ),
        useRichText: true,
      };
    })
    .filter((card) => !!card);

  return (
    <StyledImageAndCtaCards
      id={image_and_cta_cards.html_id}
      data-cy="ImageAndCtaCards"
      heading={image_and_cta_cards.heading}
      intro={image_and_cta_cards.intro ?? undefined}
      cards={cards}
    />
  );
};
export default React.memo(ImageAndCtaCardsBlock);
